import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, CopySimple, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";
import MerchantLayout from "@/components/MerchantLayout";
import TablePagination from "@/components/TablePagination";
import { MERCHANT } from "@/lib/routes";
import { mApi } from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiErrors";
import {
  addressUrl,
  fetchMerchantCryptoDeposits,
  tokenContractForNetwork,
  txUrl,
} from "@/lib/crypto";
import { StatusPill } from "@/components/Layout";

const fmt$ = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const PAGE_SIZE = 25;

function graceUsageSubtext(g) {
  const used = g.free_slots_used ?? Math.min(g.cards_used ?? 0, g.free_cards ?? 0);
  return `${used} of ${g.free_cards} free used`;
}

const fmtDelta = (v) => {
  const n = Number(v || 0);
  const abs = fmt$(Math.abs(n));
  if (n < 0) return `-${abs}`;
  if (n > 0) return `+${abs}`;
  return abs;
};

const shortAddr = (a) => {
  if (!a) return "—";
  const s = String(a);
  if (s.length < 14) return s;
  return `${s.slice(0, 8)}…${s.slice(-6)}`;
};

const shortHash = (h) => {
  if (!h) return "—";
  const s = String(h);
  if (s.length < 16) return s;
  return `${s.slice(0, 10)}…${s.slice(-6)}`;
};

async function copyText(label, value) {
  if (!value) return;
  try {
    await navigator.clipboard.writeText(value);
    toast.success(`${label} copied`);
  } catch {
    toast.error("Could not copy");
  }
}

export default function MerchantWallet() {
  const [balance, setBalance] = useState(0);
  const [currency, setCurrency] = useState("USD");
  const [issuanceGrace, setIssuanceGrace] = useState([]);
  const [bscAddress, setBscAddress] = useState(null);
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(PAGE_SIZE);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(0);
  const [loading, setLoading] = useState(true);

  const [cryptoItems, setCryptoItems] = useState([]);
  const [cryptoPage, setCryptoPage] = useState(1);
  const [cryptoLimit, setCryptoLimit] = useState(PAGE_SIZE);
  const [cryptoTotal, setCryptoTotal] = useState(0);
  const [cryptoPages, setCryptoPages] = useState(0);
  const [cryptoLoading, setCryptoLoading] = useState(true);
  const [cryptoError, setCryptoError] = useState(null);

  const network = "BSC";
  const tokenContract = tokenContractForNetwork("mainnet");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, w] = await Promise.all([
        mApi.get("/merchant/balance-funding", { params: { page, limit } }),
        mApi.get("/merchant/wallet"),
      ]);
      setBalance(r.data.balance ?? w.data.balance ?? 0);
      setCurrency(r.data.currency ?? w.data.currency ?? "USD");
      setIssuanceGrace(w.data.issuance_grace ?? []);
      setBscAddress(w.data.bsc_wallet_address || null);
      setItems(r.data.items ?? []);
      setTotal(r.data.total ?? 0);
      setPages(r.data.pages ?? 0);
    } finally {
      setLoading(false);
    }
  }, [page, limit]);

  const loadCrypto = useCallback(async () => {
    setCryptoLoading(true);
    setCryptoError(null);
    try {
      const data = await fetchMerchantCryptoDeposits({ page: cryptoPage, limit: cryptoLimit });
      setCryptoItems(data.items);
      setCryptoTotal(data.total);
      setCryptoPages(data.pages);
    } catch (err) {
      setCryptoError(apiErrorMessage(err, "Could not load crypto deposits"));
      setCryptoItems([]);
      setCryptoTotal(0);
      setCryptoPages(0);
    } finally {
      setCryptoLoading(false);
    }
  }, [cryptoPage, cryptoLimit]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [limit]);
  useEffect(() => { loadCrypto(); }, [loadCrypto]);
  useEffect(() => { setCryptoPage(1); }, [cryptoLimit]);

  return (
    <MerchantLayout>
      <div className="p-4 sm:p-6 lg:p-10" data-testid="m-wallet-page">
        <h1 className="font-display text-3xl tracking-tight font-medium mb-1">Wallet</h1>
        <p className="text-sm text-zinc-500 mb-6">
          Platform balance for issuance fees and card loads, plus your dedicated BSC USDT deposit address.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          <div className="bg-white border border-zinc-200 rounded-sm p-5">
            <div className="text-[10px] uppercase tracking-[0.05em] font-semibold text-zinc-500">Available balance</div>
            <div className="mt-2 font-display text-3xl tracking-tight tabular-nums">{fmt$(balance)}</div>
            <div className="text-xs text-zinc-500 mt-1">{currency}</div>
          </div>
          {issuanceGrace.length > 0 && issuanceGrace.map((g) => (
            <div key={g.scope_key} className="bg-white border border-zinc-200 rounded-sm p-5" data-testid="m-free-cards-remaining">
              <div className="text-[10px] uppercase tracking-[0.05em] font-semibold text-zinc-500">Free cards remaining</div>
              <div className="mt-2 font-display text-3xl tracking-tight tabular-nums">
                {g.remaining_free_cards > 0 ? (
                  <span className="text-emerald-700">{g.remaining_free_cards}</span>
                ) : (
                  <span className="text-zinc-400">0</span>
                )}
              </div>
              <div className="text-xs text-zinc-500 mt-1">
                {graceUsageSubtext(g)}
              </div>
            </div>
          ))}
          <div className="bg-white border border-zinc-200 rounded-sm p-5 flex flex-col justify-center">
            <p className="text-sm text-zinc-600">
              Admin top-ups and confirmed BSC USDT deposits both credit this balance for issuing cards and loads.
            </p>
            <Link to={MERCHANT.cards} className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-zinc-950 hover:underline">
              Issue cards <ArrowUpRight size={12} />
            </Link>
          </div>
        </div>

        <div className="bg-white border border-zinc-200 rounded-sm mb-6" data-testid="m-bsc-deposit" id="crypto-deposits">
          <div className="px-5 py-4 border-b border-zinc-100 flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="font-display text-lg tracking-tight font-medium">BSC USDT deposit</div>
              <p className="text-sm text-zinc-500 mt-0.5">
                Send BEP-20 USDT only. Funds credit your platform balance after confirmations.
              </p>
            </div>
            <span className="text-[10px] uppercase tracking-[0.05em] font-semibold px-2 py-1 rounded-sm bg-zinc-100 text-zinc-700 border border-zinc-200">
              {network} · BEP-20
            </span>
          </div>

          <div className="p-5 space-y-4">
            <div className="flex gap-3 rounded-sm border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950">
              <Warning size={18} className="shrink-0 mt-0.5" weight="fill" />
              <div>
                <p className="font-medium">Network warning</p>
                <p className="mt-0.5 text-amber-900/80">
                  Send only USDT on Binance Smart Chain (BEP-20). USDT on Ethereum, Tron, Solana, or Polygon to this address will be lost permanently.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.05em] font-semibold text-zinc-500 mb-1.5">Deposit address</div>
                {bscAddress ? (
                  <div className="flex items-stretch gap-2">
                    <code className="flex-1 min-w-0 break-all text-xs sm:text-sm font-mono bg-zinc-50 border border-zinc-200 rounded-sm px-3 py-2.5">
                      {bscAddress}
                    </code>
                    <button
                      type="button"
                      onClick={() => copyText("Address", bscAddress)}
                      className="shrink-0 h-auto px-3 inline-flex items-center gap-1.5 text-sm border border-zinc-200 rounded-sm hover:bg-zinc-50"
                      data-testid="m-copy-bsc-address"
                    >
                      <CopySimple size={16} /> Copy
                    </button>
                  </div>
                ) : (
                  <p className="text-sm text-zinc-500 py-2">
                    {loading ? "Loading address…" : "Deposit address not provisioned yet. Refresh after wallet setup is enabled for your account."}
                  </p>
                )}
                {bscAddress && (
                  <a
                    href={addressUrl(bscAddress, "mainnet")}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 mt-2 text-xs text-zinc-600 hover:underline"
                  >
                    View on BscScan <ArrowUpRight size={11} />
                  </a>
                )}
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between gap-3 border-b border-zinc-100 py-2">
                  <span className="text-zinc-500">Asset</span>
                  <span className="font-medium">USDT</span>
                </div>
                <div className="flex justify-between gap-3 border-b border-zinc-100 py-2">
                  <span className="text-zinc-500">Network</span>
                  <span className="font-medium">Binance Smart Chain (BEP-20)</span>
                </div>
                <div className="flex justify-between gap-3 border-b border-zinc-100 py-2">
                  <span className="text-zinc-500">Confirmations</span>
                  <span className="font-medium">12 blocks</span>
                </div>
                <div className="flex justify-between gap-3 py-2 items-start">
                  <span className="text-zinc-500 shrink-0">Token contract</span>
                  <button
                    type="button"
                    onClick={() => copyText("Contract", tokenContract)}
                    className="text-right font-mono text-xs text-zinc-700 hover:underline break-all"
                  >
                    {shortAddr(tokenContract)}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="px-5 py-3 border-t border-zinc-100 font-display text-base tracking-tight font-medium">
            Crypto deposit history
            <span className="ml-2 text-sm font-normal text-zinc-500 tabular-nums">({cryptoTotal})</span>
          </div>
          {cryptoError && (
            <div className="mx-5 mb-3 text-sm text-red-700 bg-red-50 border border-red-100 rounded-sm px-3 py-2">
              {cryptoError}
            </div>
          )}
          <div className="table-scroll">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-[0.05em] font-semibold text-zinc-500 border-b border-zinc-100">
                  <th className="px-5 py-3">When</th>
                  <th className="px-5 py-3 text-right">Amount</th>
                  <th className="px-5 py-3">Status</th>
                  <th className="px-5 py-3">Tx</th>
                </tr>
              </thead>
              <tbody>
                {cryptoLoading && (
                  <tr><td colSpan={4} className="px-5 py-12 text-center text-xs text-zinc-500">Loading…</td></tr>
                )}
                {!cryptoLoading && cryptoItems.map((d) => {
                  const href = txUrl(d.tx_hash, d.network);
                  return (
                    <tr key={d.id} className="border-b border-zinc-50">
                      <td className="px-5 py-3 text-xs text-zinc-500">{(d.created_at || "").toString().slice(0, 19).replace("T", " ")}</td>
                      <td className="px-5 py-3 text-right font-mono tabular-nums text-emerald-700">
                        {fmt$(d.net_amount ?? d.amount)} {d.asset || "USDT"}
                      </td>
                      <td className="px-5 py-3"><StatusPill value={d.status} /></td>
                      <td className="px-5 py-3 font-mono text-xs">
                        {href ? (
                          <a href={href} target="_blank" rel="noreferrer" className="hover:underline text-zinc-700">
                            {shortHash(d.tx_hash)}
                          </a>
                        ) : shortHash(d.tx_hash)}
                      </td>
                    </tr>
                  );
                })}
                {!cryptoLoading && cryptoItems.length === 0 && !cryptoError && (
                  <tr>
                    <td colSpan={4} className="px-5 py-12 text-center text-xs text-zinc-500">
                      No on-chain deposits yet. Send USDT BEP-20 to your address above.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <TablePagination
            page={cryptoPage}
            pages={cryptoPages}
            total={cryptoTotal}
            limit={cryptoLimit}
            loading={cryptoLoading}
            onPageChange={setCryptoPage}
            onLimitChange={setCryptoLimit}
          />
        </div>

        <div className="bg-white border border-zinc-200 rounded-sm ">
          <div className="px-5 py-4 border-b border-zinc-100 font-display text-lg tracking-tight font-medium">
            Admin funding history
            <span className="ml-2 text-sm font-normal text-zinc-500 tabular-nums">({total})</span>
          </div>
          <div className="table-scroll">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-[0.05em] font-semibold text-zinc-500 border-b border-zinc-100">
                <th className="px-5 py-3">Date</th>
                <th className="px-5 py-3 text-right">Amount</th>
                <th className="px-5 py-3 text-right">Balance after</th>
                <th className="px-5 py-3">Note</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={4} className="px-5 py-12 text-center text-xs text-zinc-500">Loading…</td></tr>
              )}
              {!loading && items.map((f) => (
                <tr key={f.id} className="border-b border-zinc-50">
                  <td className="px-5 py-3 text-xs text-zinc-500">{(f.created_at || "").slice(0, 19).replace("T", " ")}</td>
                  <td className={`px-5 py-3 text-right font-mono tabular-nums ${Number(f.amount) < 0 ? "text-red-700" : "text-emerald-700"}`}>
                    {fmtDelta(f.amount)}
                  </td>
                  <td className="px-5 py-3 text-right font-mono tabular-nums">{fmt$(f.balance_after)}</td>
                  <td className="px-5 py-3 text-xs text-zinc-600">{f.note || "—"}</td>
                </tr>
              ))}
              {!loading && items.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-5 py-12 text-center text-xs text-zinc-500">
                    No funding recorded yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          </div>
          <TablePagination
            page={page}
            pages={pages}
            total={total}
            limit={limit}
            loading={loading}
            onPageChange={setPage}
            onLimitChange={setLimit}
          />
        </div>
      </div>
    </MerchantLayout>
  );
}
